import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import { useRouter } from "utils";
import { Button, Typography, Grid, Card, CardContent, Divider, TextField, CircularProgress } from "@material-ui/core";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import { ADD_TIMESHEET_ROUTE } from "../timesheet-routes";
import { onAddTimesheetStartTimer, onAddTimesheetStopTimer, onAddTimesheetValueChange } from "../timesheet-actions";
import { getCurrentPosition } from "features/geolocation";
import { useSelector } from "react-redux";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
	root: {
		margin: theme.spacing(2, 0),
	},
	message: {
		fontSize: 14,
		color: theme.palette.text.primary,
		textAlign: "center",
	},
	timer: {
		fontWeight: 600,
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		fontSize: "1.25rem",
	},
	label: {
		color: "#999999",
	},
	panel: {
		marginTop: theme.spacing(2),
	},
	notesPanel: {
		marginTop: theme.spacing(2),
	},
	panelContent: {
		textAlign: "center",
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
	},
	notesPanelContent: {
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(2),
	},
	divider: {
		margin: theme.spacing(2, 0),
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "center",
		marginTop: theme.spacing(1),
	},
	outsideButtonContainer: {
		display: "flex",
		justifyContent: "center",
		marginTop: theme.spacing(1),
	},
	afterSiteActions: {
		display: "flex",
		justifyContent: "stretch",
		width: "100%",
	},
	icon: {
		marginRight: theme.spacing(1),
		fontSize: 28,
	},
	busySpinner: {
		marginRight: theme.spacing(1),
		color: "inherit",
	},
	startButton: {
		width: "100%",
		marginTop: 0,
		marginBottom: 0,
		padding: theme.spacing(1, 4),
		background: theme.palette.success.main,
		borderColor: theme.palette.success.main,
		borderRadius: theme.shape.borderRadius,
		"&:hover": {
			background: "#388e3c",
			borderColor: "#388e3c",
		},
		[theme.breakpoints.up("sm")]: {
			maxWidth: "300px",
		},
	},
	stopButton: {
		width: "100%",
		marginTop: 0,
		marginBottom: 0,
		padding: theme.spacing(1, 4),
		background: theme.palette.error.main,
		borderColor: theme.palette.error.main,
		borderRadius: theme.shape.borderRadius,
		"&:hover": {
			background: theme.palette.error.dark,
			borderColor: theme.palette.error.dark,
		},
		[theme.breakpoints.up("sm")]: {
			maxWidth: "300px",
		},
	},
	afterSiteStartButton: {
		maxWidth: "none",
		[theme.breakpoints.up("sm")]: {
			maxWidth: "none",
		},
	},
	afterSiteStopButton: {
		maxWidth: "none",
		[theme.breakpoints.up("sm")]: {
			maxWidth: "none",
		},
	},
	notesFieldWrap: {
		textAlign: "left",
		width: "100%",
		marginTop: theme.spacing(1),
	},
	notesHelper: {
		textAlign: "left",
		marginTop: theme.spacing(0.5),
	},
}));

const mapStateToProps = ({
	profile,
	timesheetFormReducer,
}) => ({
	currentStaffId: profile.staffId,
	values: timesheetFormReducer,
});

const mapDispatchToProps = {
	onStartTimer: onAddTimesheetStartTimer,
	onStopTimer: onAddTimesheetStopTimer,
	onChange: onAddTimesheetValueChange,
};

export const TimesheetToggleForm = connect(mapStateToProps, mapDispatchToProps)(({
	values,
	currentStaffId,
	onStartTimer,
	onStopTimer,
	onChange,
	onStopped,
	showStartButton = true,
	showStopButton = true,
	actionsOnly = false,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const timerToggle = values.timesheet.toggle;
	const timerMessage = values.timerMessage;
	const timesheet = values.timesheet || {};
	const isHandlingRef = useRef(false);
	const notesSaveTimeoutRef = useRef(null);
	const hasHydratedNotesRef = useRef(false);

	// Running timer state (only while active)
	const [elapsed, setElapsed] = useState("00:00:00");
	const [notesSaveStatus, setNotesSaveStatus] = useState("");
	// Local busy flag: disable Start/Stop immediately on click while GPS/API waits
	const [isBusy, setIsBusy] = useState(false);

	useEffect(() => {
		if (!timerToggle || (!timesheet.startDateTimeUTC && (!timesheet.startDate || !timesheet.startTime))) {
			return;
		}

		const updateElapsed = () => {
			const startMoment = timesheet.startDateTimeUTC
				? moment.utc(timesheet.startDateTimeUTC).local()
				: moment(timesheet.startDate + " " + timesheet.startTime, "YYYY-MM-DD HH:mm");
			const now = moment();
			const duration = moment.duration(now.diff(startMoment));
			const hours = Math.floor(duration.asHours());
			const minutes = duration.minutes();
			const seconds = duration.seconds();
			setElapsed(
				String(hours).padStart(2, "0") +
				":" +
				String(minutes).padStart(2, "0") +
				":" +
				String(seconds).padStart(2, "0")
			);
		};

		updateElapsed();
		const interval = setInterval(updateElapsed, 1000);
		return () => clearInterval(interval);
	}, [timerToggle, timesheet.startDateTimeUTC, timesheet.startDate, timesheet.startTime]);

	useEffect(() => {
		if (!timerToggle) {
			hasHydratedNotesRef.current = false;
			setNotesSaveStatus("");
			if (notesSaveTimeoutRef.current) {
				clearTimeout(notesSaveTimeoutRef.current);
			}
			return;
		}

		if (!hasHydratedNotesRef.current) {
			hasHydratedNotesRef.current = true;
			return;
		}

		setNotesSaveStatus("");
		if (notesSaveTimeoutRef.current) {
			clearTimeout(notesSaveTimeoutRef.current);
		}

		notesSaveTimeoutRef.current = setTimeout(() => {
			setNotesSaveStatus("Saved");
		}, 900);

		return () => {
			if (notesSaveTimeoutRef.current) {
				clearTimeout(notesSaveTimeoutRef.current);
			}
		};
	}, [timerToggle, timesheet.notes]);
	
	// Get task types to check if they're available
	const taskTypeList = useSelector(({ taskTypes }) => taskTypes.data || []);
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const workTypesEnabled = featureFlags?.workTypes !== false; // Default to true if not loaded
	const hasTaskTypes = taskTypeList && taskTypeList.length > 0;

	const isActionPending = isBusy || values.isLoading;

	// Check if start button should be disabled (validation only applies to start)
	// Only require task type if Work Types feature is enabled AND task types exist
	const isStartButtonDisabled = isActionPending ||
		!values.siteId ||
		(workTypesEnabled && hasTaskTypes && !values.organisationTaskTypeId);

	// Stop button should only be disabled while pending, not for validation
	const isStopButtonDisabled = isActionPending;

	const handleTimer = async () => {
		if (isHandlingRef.current || isActionPending) return;
		isHandlingRef.current = true;
		setIsBusy(true);
		const wasStopping = timerToggle;
		try {
			const { latitude, longitude } = await getCurrentPosition();
			if (wasStopping) {
				// Stop timer, capture end geolocation at the exact moment Stop is pressed
				onStopTimer && onStopTimer({ latitude, longitude });
				if (typeof onStopped === "function") {
					onStopped();
				} else {
					const basePath = ADD_TIMESHEET_ROUTE;
					history.push(`${basePath}?stopped=1`);
				}
				// Keep isBusy until review navigation unmounts this button
			} else {
				// Start timer, capture start geolocation at the exact moment Start is pressed
				onStartTimer({
					staffId: currentStaffId,
					latitude,
					longitude,
				});
				// Redux isLoading is set synchronously; local busy can clear
				setIsBusy(false);
			}
		} catch (e) {
			setIsBusy(false);
		} finally {
			isHandlingRef.current = false;
		}
	};

	return (
		<>
			{actionsOnly ? (
				<div className={classes.afterSiteActions}>
					{!timerToggle && showStartButton && (
						<Button
							variant="contained"
							color="primary"
							onClick={handleTimer}
							disabled={isStartButtonDisabled}
							className={`${classes.startButton} ${classes.afterSiteStartButton}`}
							fullWidth
						>
							{isActionPending ? (
								<CircularProgress size={22} className={classes.busySpinner} />
							) : (
								<PlayArrowIcon className={classes.icon} />
							)}
							{isActionPending ? "Starting…" : "Start"}
						</Button>
					)}
					{timerToggle && showStopButton && (
						<Button
							variant="contained"
							color="primary"
							onClick={handleTimer}
							disabled={isStopButtonDisabled}
							className={`${classes.stopButton} ${classes.afterSiteStopButton}`}
							fullWidth
						>
							{isActionPending ? (
								<CircularProgress size={22} className={classes.busySpinner} />
							) : (
								<StopIcon className={classes.icon} />
							)}
							{isActionPending ? "Stopping…" : "Stop"}
						</Button>
					)}
				</div>
			) : (
				<>
			<Card className={classes.panel}>
				<CardContent className={classes.panelContent}>
					{timerToggle ? (
						<>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<Typography className={classes.label} variant="body2">
										Started
									</Typography>
									{timesheet.startDate && timesheet.startTime ? (
										<>
											<Typography variant="subtitle1">
												{(timesheet.startDateTimeUTC
													? moment.utc(timesheet.startDateTimeUTC).local()
													: moment(timesheet.startDate + " " + timesheet.startTime, "YYYY-MM-DD HH:mm")
												).format("h:mm a")}
											</Typography>
											<Typography variant="body2">
												{(timesheet.startDateTimeUTC
													? moment.utc(timesheet.startDateTimeUTC).local()
													: moment(timesheet.startDate + " " + timesheet.startTime, "YYYY-MM-DD HH:mm")
												).format("D MMM YYYY")}
											</Typography>
										</>
									) : (
										<Typography variant="subtitle1">- - -</Typography>
									)}
								</Grid>
								<Grid item xs={6}>
									<Typography className={classes.label} variant="body2">
										Ended
									</Typography>
							<Typography variant="subtitle1">- - -</Typography>
								</Grid>
							</Grid>
							<Divider className={classes.divider} />
							<Typography className={classes.label} variant="body2">Duration</Typography>
							<Typography variant="h4" align="center" className={classes.timer}>
								{elapsed}
							</Typography>
						</>
					) : (
						<>
							<Grid container spacing={2}>
								<Grid item xs={6}>
							<Typography className={classes.label} variant="body2">Started</Typography>
							<Typography variant="subtitle1">- - -</Typography>
								</Grid>
								<Grid item xs={6}>
							<Typography className={classes.label} variant="body2">Ended</Typography>
							<Typography variant="subtitle1">- - -</Typography>
						</Grid>
							</Grid>
					<Divider className={classes.divider} />
							<Typography className={classes.label} variant="body2">Duration</Typography>
							<Typography variant="h4" align="center" className={classes.timer}>- - -</Typography>
							<Divider className={classes.divider} />
						</>
					)}
					{!timerToggle && showStartButton && (
						<div className={classes.buttonContainer}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleTimer}
								disabled={isStartButtonDisabled}
								className={classes.startButton}
								fullWidth
							>
								{isActionPending ? (
									<CircularProgress size={22} className={classes.busySpinner} />
								) : (
									<PlayArrowIcon className={classes.icon} />
								)}
								{isActionPending ? "Starting…" : "Start"}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
			{timerToggle && (
				<Card className={classes.notesPanel}>
					<CardContent className={classes.notesPanelContent}>
						<div className={classes.notesFieldWrap}>
							<TextField
								id="timesheet_notes_active"
								fullWidth
								label="Notes"
								multiline
								rows={3}
								value={timesheet.notes || ""}
								placeholder="You can add notes here at any time."
								variant="outlined"
								helperText={notesSaveStatus}
								FormHelperTextProps={{ className: classes.notesHelper }}
								InputLabelProps={{ shrink: true }}
								inputProps={{ "aria-label": "Notes" }}
								onChange={(e) =>
									onChange({
										...values,
										timesheet: { ...timesheet, notes: e.target.value },
									})
								}
							/>
						</div>
					</CardContent>
				</Card>
			)}
			{timerToggle && showStopButton && (
				<div className={classes.outsideButtonContainer}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleTimer}
						disabled={isStopButtonDisabled}
						className={classes.stopButton}
						fullWidth
					>
						{isActionPending ? (
							<CircularProgress size={22} className={classes.busySpinner} />
						) : (
							<StopIcon className={classes.icon} />
						)}
						{isActionPending ? "Stopping…" : "Stop"}
					</Button>
				</div>
			)}
				</>
			)}
		</>
	);
});
